import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { getMainSiteUrl } from '@/lib/domain';
import DocsShareModal from '@/components/feature/DocsShareModal';
import LiveDashboard from './LiveDashboard';

// ─── Types ───────────────────────────────────────────────────────────────────
interface ZCallMeeting {
  id: number;
  user_id: number;
  title: string;
  room_id: string;
  slug: string | null;
  scheduled_at: string | null;
  duration_minutes: number;
  status: 'scheduled' | 'active' | 'ended' | 'cancelled';
  created_at: string;
}

type TabKey = 'instant' | 'schedule' | 'live' | 'list';

const DURATION_OPTIONS = [
  { value: 90, label: '1h30' },
  { value: 120, label: '2h' },
  { value: 180, label: '3h' },
  { value: 240, label: '4h' },
  { value: 360, label: '6h' },
];

/** Durée MINIMALE d'une réunion ZCall (1h30). */
const MIN_DURATION_MIN = 90;

/**
 * Marge de tolérance avant fermeture automatique (minutes) : quand l'hôte a
 * oublié de cliquer sur « Terminer », la réunion se clôture automatiquement
 * une fois DURÉE + MARGE écoulées depuis le début.
 */
const AUTO_CLOSE_GRACE_MIN = 15;

/** Détermine le début effectif d'une réunion (heure programmée sinon création). */
function meetingStartMs(m: { scheduled_at: string | null; created_at: string }): number {
  const raw = m.scheduled_at || m.created_at;
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : Date.now();
}

/**
 * Domaine public de la salle ZCall.
 *
 * ⚠️ Important : le lien d'invitation est TOUJOURS construit sur le domaine
 * principal de la plateforme (ex: https://zifek.fr/zcall/xxx) et non sur le
 * sous-domaine de la boutique. Auparavant, un lien du type
 * `https://maboutique.zifek.fr/zcall/xxx` chargeait la vitrine complète du
 * tenant (très lourde) au lieu de la salle, ce qui aboutissait à un
 * « site inaccessible / délai dépassé » pour les invités. Sur le domaine
 * principal, la salle ZCall s'ouvre directement, sans chargement de vitrine.
 */
async function resolveInviteDomain(): Promise<string> {
  return getMainSiteUrl();
}

const STATUS_STYLES: Record<string, string> = {
  scheduled: 'bg-amber-50 text-amber-700 border-amber-200',
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  ended: 'bg-background-100 text-foreground-400 border-background-200',
  cancelled: 'bg-red-50 text-red-500 border-red-200',
};

const STATUS_LABELS: Record<string, string> = {
  scheduled: 'Programmée',
  active: 'En cours',
  ended: 'Terminée',
  cancelled: 'Annulée',
};

// ─── Main Page ───────────────────────────────────────────────────────────────
export default function ZCallPage() {
  const { user } = useAuth();
  const userId = user?.id;

  const [activeTab, setActiveTab] = useState<TabKey>('instant');
  const [meetings, setMeetings] = useState<ZCallMeeting[]>([]);
  const [loading, setLoading] = useState(true);

  // Domaine public de la boutique (sous-domaine ou domaine personnalisé)
  const [inviteDomain, setInviteDomain] = useState<string>('');

  // Instant form
  const [instantTitle, setInstantTitle] = useState('');
  const [instantDuration, setInstantDuration] = useState(MIN_DURATION_MIN);
  const [instantRoomId, setInstantRoomId] = useState<string | null>(null);
  const [instantSlug, setInstantSlug] = useState<string | null>(null);
  const [instantMeetingId, setInstantMeetingId] = useState<number | null>(null);

  // Schedule form
  const [schedTitle, setSchedTitle] = useState('');
  const [schedDate, setSchedDate] = useState('');
  const [schedTime, setSchedTime] = useState('10:00');
  const [schedDuration, setSchedDuration] = useState(MIN_DURATION_MIN);

  // Copy feedback
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [copiedInstant, setCopiedInstant] = useState(false);

  // Partage de documents (dossier d'accès de la réunion)
  const [shareRoom, setShareRoom] = useState<{ roomId: string; title: string } | null>(null);

  // Les liens d'invitation sont TOUJOURS sur le domaine principal (zifek.fr) :
  // on n'attend pas la résolution asynchrone pour éviter tout lien de boutique.
  const domain = inviteDomain || getMainSiteUrl();

  // ──────────────────────────────────────────────────────────────────────────
  const fetchMeetings = useCallback(async () => {
    if (!userId) return;
    const { data, error } = await supabase
      .from('zcall_meetings')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    if (!error && data) {
      setMeetings(data as ZCallMeeting[]);
    }
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    if (userId) fetchMeetings();
  }, [userId, fetchMeetings]);

  // Résout le domaine public de la salle au chargement
  useEffect(() => {
    if (!user) return;
    resolveInviteDomain().then((d) => setInviteDomain(d));
  }, [user]);

  // ─── Instant Meeting ────────────────────────────────────────────────────
  const createInstantMeeting = async () => {
    if (!instantTitle.trim()) return;
    const roomId = generateRoomId();
    const slug = generateSlug(instantTitle);
    const { data, error } = await supabase.from('zcall_meetings').insert({
      user_id: userId,
      title: instantTitle.trim(),
      room_id: roomId,
      slug,
      scheduled_at: null,
      duration_minutes: Math.max(MIN_DURATION_MIN, instantDuration),
      status: 'active',
    }).select('id').single();
    if (error || !data) {
      console.error('Erreur création réunion:', error);
      alert('Erreur lors de la création : ' + (error?.message || 'inconnue'));
      return;
    }
    setInstantMeetingId(data.id);
    setInstantRoomId(roomId);
    setInstantSlug(slug);
    fetchMeetings();
  };

  const joinInstant = () => {
    if (instantSlug) {
      window.open(getMeetingUrl(domain, { slug: instantSlug, room_id: instantRoomId || '' }), '_blank');
    }
  };

  // ─── Schedule Meeting ───────────────────────────────────────────────────
  const scheduleMeeting = async () => {
    if (!schedTitle.trim() || !schedDate || !schedTime) return;
    const roomId = generateRoomId();
    const slug = generateSlug(schedTitle);
    const scheduledAt = `${schedDate}T${schedTime}:00`;
    const { error } = await supabase.from('zcall_meetings').insert({
      user_id: userId,
      title: schedTitle.trim(),
      room_id: roomId,
      slug,
      scheduled_at: scheduledAt,
      duration_minutes: Math.max(MIN_DURATION_MIN, schedDuration),
      status: 'scheduled',
    });
    if (error) {
      console.error('Erreur programmation réunion:', error);
      alert('Erreur lors de la programmation : ' + error.message);
      return;
    }
    setSchedTitle('');
    setSchedDate('');
    setSchedTime('10:00');
    setSchedDuration(MIN_DURATION_MIN);
    fetchMeetings();
  };

  // ─── Actions ────────────────────────────────────────────────────────────
  const copyLink = (meeting: ZCallMeeting) => {
    navigator.clipboard.writeText(getMeetingUrl(domain, meeting));
    setCopiedId(meeting.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const copyInstantLink = () => {
    if (instantSlug) {
      navigator.clipboard.writeText(
        getMeetingUrl(domain, { slug: instantSlug, room_id: instantRoomId || '' }),
      );
      setCopiedInstant(true);
      setTimeout(() => setCopiedInstant(false), 2000);
    }
  };

  const cancelMeeting = async (meetingId: number) => {
    const { error } = await supabase
      .from('zcall_meetings')
      .update({ status: 'cancelled' })
      .eq('id', meetingId);
    if (error) {
      console.error('Erreur annulation:', error);
      alert('Erreur : ' + error.message);
      return;
    }
    fetchMeetings();
  };

  const deleteMeeting = async (meetingId: number) => {
    const { error } = await supabase.from('zcall_meetings').delete().eq('id', meetingId);
    if (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur : ' + error.message);
      return;
    }
    fetchMeetings();
  };

  /** Ferme une réunion en cours (statut → ended). */
  const endMeeting = async (meetingId: number) => {
    const { error } = await supabase
      .from('zcall_meetings')
      .update({ status: 'ended' })
      .eq('id', meetingId);
    if (error) {
      console.error('Erreur fermeture:', error);
      alert('Erreur : ' + error.message);
      return;
    }
    // Si c'était la réunion instantanée affichée, on réinitialise l'écran
    if (meetingId === instantMeetingId) {
      setInstantMeetingId(null);
      setInstantRoomId(null);
      setInstantSlug(null);
      setInstantTitle('');
    }
    fetchMeetings();
  };

  // ─── Fermeture automatique (durée + date de début) ──────────────────────
  // Si l'hôte a oublié de cliquer sur « Terminer », on clôture dès que
  // DÉBUT + DURÉE + MARGE est dépassé. Le début = heure programmée si la
  // réunion était planifiée, sinon la date de création.
  useEffect(() => {
    if (!userId) return;
    const autoCloseExpired = async () => {
      const { data } = await supabase
        .from('zcall_meetings')
        .select('id, status, created_at, scheduled_at, duration_minutes')
        .eq('user_id', userId)
        .eq('status', 'active');
      if (!data) return;
      const now = Date.now();
      const expiredIds = (data as Pick<ZCallMeeting, 'id' | 'created_at' | 'scheduled_at' | 'duration_minutes'>[])
        .filter((m) => {
          const end = meetingStartMs(m) + (m.duration_minutes || MIN_DURATION_MIN) * 60 * 1000
            + AUTO_CLOSE_GRACE_MIN * 60 * 1000;
          return now > end;
        })
        .map((m) => m.id);
      if (expiredIds.length > 0) {
        await supabase
          .from('zcall_meetings')
          .update({ status: 'ended' })
          .in('id', expiredIds);
        fetchMeetings();
      }
    };
    autoCloseExpired();
  }, [userId, fetchMeetings]);

  // ──────────────────────────────────────────────────────────────────────────
  const upcomingMeetings = meetings.filter((m) => m.status === 'scheduled');
  const pastMeetings = meetings.filter((m) => m.status !== 'scheduled');

  const instantLink =
    instantSlug != null
      ? getMeetingUrl(domain, { slug: instantSlug, room_id: instantRoomId || '' })
      : null;

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl overflow-hidden flex items-center justify-center">
            <img
              src="https://storage.helloreaddy.io/project_files/59392c9e-e303-496e-bac1-59ba5941cf65/07452721-5131-4b23-922e-acc73d68a0a9_compressed_zifek.webp"
              alt="ZIFEK"
              title="ZIFEK"
              className="w-full h-full object-cover"
            />
          </div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">
            ZCall
          </h2>
        </div>
        <p className="text-sm text-foreground-500 mt-1">Visioconférence instantanée ou programmée</p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 mb-6 w-fit">
        {([
          { key: 'instant' as TabKey, icon: 'ri-flashlight-line', label: 'Instantanée' },
          { key: 'schedule' as TabKey, icon: 'ri-calendar-event-line', label: 'Programmer' },
          { key: 'live' as TabKey, icon: 'ri-live-line', label: 'Live' },
          { key: 'list' as TabKey, icon: 'ri-list-check-2', label: `Mes réunions${meetings.length > 0 ? ` (${meetings.length})` : ''}` },
        ]).map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap transition-colors ${
              activeTab === tab.key
                ? 'bg-background-50 text-foreground-950 shadow-sm'
                : 'text-foreground-500 hover:text-foreground-700'
            }`}
          >
            <i className={`${tab.icon} text-base`}></i>
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* ─── Tab: Instant ──────────────────────────────────────────────── */}
      {activeTab === 'instant' && (
        <div className="space-y-6">
          {!instantSlug ? (
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center">
                  <i className="ri-flashlight-line text-xl text-primary-500"></i>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground-900">Réunion instantanée</h3>
                  <p className="text-xs text-foreground-500">Démarrez une visioconférence maintenant</p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1.5">Titre de la réunion</label>
                  <input
                    type="text"
                    value={instantTitle}
                    onChange={(e) => setInstantTitle(e.target.value)}
                    placeholder="Ex: Brief équipe, Appel client..."
                    className="w-full px-4 py-2.5 text-sm border border-background-200/70 rounded-xl bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1.5">Durée estimée</label>
                  <div className="flex items-center gap-2 flex-wrap">
                    {DURATION_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => setInstantDuration(opt.value)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap transition-colors ${
                          instantDuration === opt.value
                            ? 'bg-primary-500 text-background-50'
                            : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <button
                  onClick={createInstantMeeting}
                  disabled={!instantTitle.trim()}
                  className="w-full py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold cursor-pointer hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <i className="ri-vidicon-line"></i>
                  Démarrer la réunion maintenant
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-background-50 border border-accent-200/50 rounded-xl p-6 space-y-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                  <i className="ri-check-line text-xl text-emerald-500"></i>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground-900">Réunion créée !</h3>
                  <p className="text-xs text-foreground-500">{instantTitle}</p>
                </div>
              </div>

              <div className="p-4 bg-background-100 rounded-xl">
                <p className="text-xs font-medium text-foreground-500 mb-2">Lien d'invitation :</p>
                <div className="flex items-center gap-2">
                  <input
                    readOnly
                    value={instantLink || ''}
                    className="flex-1 px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-xs font-mono text-foreground-700"
                  />
                  <button
                    onClick={copyInstantLink}
                    className="px-4 py-2 bg-secondary-50 text-secondary-700 rounded-full text-xs font-medium cursor-pointer hover:bg-secondary-100 transition-colors whitespace-nowrap"
                  >
                    {copiedInstant ? (
                      <><i className="ri-check-line mr-1"></i>Copié !</>
                    ) : (
                      <><i className="ri-file-copy-line mr-1"></i>Copier</>
                    )}
                  </button>
                </div>
                <p className="text-[11px] text-foreground-400 mt-2">
                  Partagez ce lien à vos participants pour qu'ils rejoignent la visioconférence.
                </p>
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                <button
                  onClick={joinInstant}
                  className="flex-1 py-3 bg-emerald-500 text-white rounded-full text-sm font-semibold cursor-pointer hover:bg-emerald-600 transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <i className="ri-vidicon-line"></i>
                  Lancer la visioconférence
                </button>
                <button
                  onClick={() => instantMeetingId && endMeeting(instantMeetingId)}
                  className="px-6 py-3 bg-red-50 text-red-600 rounded-full text-sm font-semibold cursor-pointer hover:bg-red-100 transition-colors flex items-center justify-center gap-1.5 whitespace-nowrap"
                >
                  <i className="ri-stop-circle-line"></i>
                  Terminer
                </button>
                <button
                  onClick={() => { setInstantRoomId(null); setInstantSlug(null); setInstantTitle(''); setInstantMeetingId(null); }}
                  className="px-6 py-3 bg-background-100 text-foreground-600 rounded-full text-sm font-medium cursor-pointer hover:bg-background-200/70 transition-colors whitespace-nowrap"
                >
                  Nouvelle réunion
                </button>
              </div>

              <button
                onClick={() => instantRoomId && setShareRoom({ roomId: instantRoomId, title: instantTitle || 'Documents de la réunion' })}
                className="w-full py-3 bg-secondary-50 text-secondary-700 rounded-full text-sm font-semibold cursor-pointer hover:bg-secondary-100 transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
              >
                <i className="ri-folder-shared-line"></i>
                Préparer les documents de la réunion
              </button>
              <p className="text-[11px] text-foreground-400 text-center -mt-2">
                Sélectionnez des dossiers/fichiers de vos Documents : ils seront accessibles et projetables pendant la conférence.
              </p>
            </div>
          )}

          {/* Features */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { icon: 'ri-group-line', title: 'Participants illimités', desc: 'Invitez autant de personnes que nécessaire' },
              { icon: 'ri-shield-check-line', title: 'Crypté de bout en bout', desc: 'Vos conversations sont sécurisées' },
              { icon: 'ri-smartphone-line', title: 'Tous les appareils', desc: 'Mobile, tablette ou ordinateur' },
            ].map((f, i) => (
              <div key={i} className="bg-background-50 border border-background-200/70 rounded-xl p-4 flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center shrink-0">
                  <i className={`${f.icon} text-foreground-500`}></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-800">{f.title}</p>
                  <p className="text-[11px] text-foreground-500 mt-0.5">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ─── Tab: Schedule ─────────────────────────────────────────────── */}
      {activeTab === 'schedule' && (
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-secondary-50 flex items-center justify-center">
              <i className="ri-calendar-event-line text-xl text-secondary-600"></i>
            </div>
            <div>
              <h3 className="text-base font-semibold text-foreground-900">Programmer une réunion</h3>
              <p className="text-xs text-foreground-500">Planifiez une visioconférence pour plus tard</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Titre de la réunion *</label>
              <input
                type="text"
                value={schedTitle}
                onChange={(e) => setSchedTitle(e.target.value)}
                placeholder="Ex: Démo produit, Réunion projet..."
                className="w-full px-4 py-2.5 text-sm border border-background-200/70 rounded-xl bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300 transition-colors"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1.5">Date *</label>
                <input
                  type="date"
                  value={schedDate}
                  onChange={(e) => setSchedDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-2.5 text-sm border border-background-200/70 rounded-xl bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300 transition-colors cursor-pointer"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1.5">Heure *</label>
                <input
                  type="time"
                  value={schedTime}
                  onChange={(e) => setSchedTime(e.target.value)}
                  className="w-full px-4 py-2.5 text-sm border border-background-200/70 rounded-xl bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300 transition-colors cursor-pointer"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Durée estimée</label>
              <div className="flex items-center gap-2 flex-wrap">
                {DURATION_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSchedDuration(opt.value)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap transition-colors ${
                      schedDuration === opt.value
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={scheduleMeeting}
              disabled={!schedTitle.trim() || !schedDate || !schedTime}
              className="w-full py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold cursor-pointer hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 whitespace-nowrap"
            >
              <i className="ri-calendar-check-line"></i>
              Programmer la réunion
            </button>
          </div>
        </div>
      )}

      {/* ─── Tab: Live ──────────────────────────────────────────────────── */}
      {activeTab === 'live' && user && <LiveDashboard userId={user.id} />}

      {/* ─── Tab: List ──────────────────────────────────────────────────── */}
      {activeTab === 'list' && (
        <div className="space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
            </div>
          ) : meetings.length === 0 ? (
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-10 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
                <i className="ri-vidicon-line text-3xl text-foreground-300"></i>
              </div>
              <h3 className="text-base font-semibold text-foreground-700 mb-2">Aucune réunion</h3>
              <p className="text-sm text-foreground-500 max-w-md mx-auto">
                Créez une réunion instantanée ou programmez-en une pour plus tard.
              </p>
            </div>
          ) : (
            <>
              {/* Upcoming / Scheduled */}
              {upcomingMeetings.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-foreground-500 uppercase tracking-wide mb-3">
                    <i className="ri-calendar-event-line mr-1.5"></i>
                    À venir ({upcomingMeetings.length})
                  </h3>
                  <div className="space-y-3">
                    {upcomingMeetings.map((m) => (
                      <MeetingCard
                        key={m.id}
                        meeting={m}
                        inviteDomain={domain}
                        copyLink={copyLink}
                        cancelMeeting={cancelMeeting}
                        deleteMeeting={deleteMeeting}
                        endMeeting={endMeeting}
                        copiedId={copiedId}
                        onShare={setShareRoom}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Past / Active / Cancelled */}
              {pastMeetings.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-foreground-500 uppercase tracking-wide mb-3">
                    <i className="ri-history-line mr-1.5"></i>
                    Historique ({pastMeetings.length})
                  </h3>
                  <div className="space-y-3">
                    {pastMeetings.map((m) => (
                      <MeetingCard
                        key={m.id}
                        meeting={m}
                        inviteDomain={domain}
                        copyLink={copyLink}
                        cancelMeeting={cancelMeeting}
                        deleteMeeting={deleteMeeting}
                        endMeeting={endMeeting}
                        copiedId={copiedId}
                        onShare={setShareRoom}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {shareRoom && user && (
        <DocsShareModal
          userId={user.id}
          userName={user.name || user.user_name || 'Utilisateur'}
          title={shareRoom.title}
          roomId={shareRoom.roomId}
          onClose={() => setShareRoom(null)}
        />
      )}
    </div>
  );
}

// ─── Meeting Card ─────────────────────────────────────────────────────────────
function MeetingCard({
  meeting,
  inviteDomain,
  copyLink,
  cancelMeeting,
  deleteMeeting,
  endMeeting,
  copiedId,
  onShare,
}: {
  meeting: ZCallMeeting;
  inviteDomain: string;
  copyLink: (meeting: ZCallMeeting) => void;
  cancelMeeting: (meetingId: number) => void;
  deleteMeeting: (meetingId: number) => void;
  endMeeting: (meetingId: number) => void;
  copiedId: number | null;
  onShare: (room: { roomId: string; title: string }) => void;
}) {
  const isScheduled = meeting.status === 'scheduled';
  const isActive = meeting.status === 'active';

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-xl p-4 hover:border-background-300/60 transition-colors group">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
            isActive ? 'bg-emerald-50' : isScheduled ? 'bg-amber-50' : 'bg-background-100'
          }`}>
            <i className={`${
              isActive ? 'ri-vidicon-line text-emerald-600' :
              isScheduled ? 'ri-calendar-event-line text-amber-600' :
              'ri-checkbox-circle-line text-foreground-400'
            }`}></i>
          </div>
          <div className="min-w-0">
            <h4 className="text-sm font-semibold text-foreground-900 truncate">{meeting.title}</h4>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {meeting.scheduled_at ? (
                <span className="text-xs text-foreground-500">
                  <i className="ri-time-line mr-1"></i>
                  {formatDateTime(meeting.scheduled_at)}
                </span>
              ) : (
                <span className="text-xs text-foreground-500">
                  <i className="ri-flashlight-line mr-1"></i>
                  Instantanée
                </span>
              )}
              <span className="text-xs text-foreground-400">·</span>
              <span className="text-xs text-foreground-500">{meeting.duration_minutes} min</span>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${STATUS_STYLES[meeting.status] || ''}`}>
                {STATUS_LABELS[meeting.status] || meeting.status}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {(isScheduled || isActive) && (
            <a
              href={getMeetingUrl(inviteDomain, meeting)}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-emerald-500 text-white rounded-full text-xs font-semibold cursor-pointer hover:bg-emerald-600 transition-colors flex items-center gap-1.5 whitespace-nowrap"
            >
              <i className="ri-vidicon-line text-sm"></i>
              Lancer
            </a>
          )}
          {isActive && (
            <button
              onClick={() => endMeeting(meeting.id)}
              className="px-4 py-2 bg-red-50 text-red-600 rounded-full text-xs font-semibold cursor-pointer hover:bg-red-100 transition-colors flex items-center gap-1.5 whitespace-nowrap"
              title="Fermer la réunion"
            >
              <i className="ri-stop-circle-line text-sm"></i>
              Terminer
            </button>
          )}
          <div className="flex items-center gap-1.5 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
            <button
              onClick={() => onShare({ roomId: meeting.room_id, title: meeting.title })}
              className="px-3 py-1.5 bg-accent-50 text-accent-700 rounded-full text-xs font-medium cursor-pointer hover:bg-accent-100 transition-colors whitespace-nowrap"
              title="Documents de la réunion"
            >
              <i className="ri-folder-shared-line mr-1"></i>Docs
            </button>
            <button
              onClick={() => copyLink(meeting)}
              className="px-3 py-1.5 bg-secondary-50 text-secondary-700 rounded-full text-xs font-medium cursor-pointer hover:bg-secondary-100 transition-colors whitespace-nowrap"
            >
              {copiedId === meeting.id ? (
                <><i className="ri-check-line mr-1"></i>Copié !</>
              ) : (
                <><i className="ri-link mr-1"></i>Copier le lien</>
              )}
            </button>
            {isScheduled && (
              <button
                onClick={() => cancelMeeting(meeting.id)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-red-50 text-red-400 cursor-pointer hover:bg-red-100 hover:text-red-600 transition-colors"
                title="Annuler"
              >
                <i className="ri-close-line text-sm"></i>
              </button>
            )}
            {(meeting.status === 'ended' || meeting.status === 'cancelled') && (
              <button
                onClick={() => deleteMeeting(meeting.id)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-red-50 text-red-400 cursor-pointer hover:bg-red-100 hover:text-red-600 transition-colors"
                title="Supprimer"
              >
                <i className="ri-delete-bin-line text-sm"></i>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────────────
function generateRoomId(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  const seg = (len: number): string => {
    const bytes = new Uint8Array(len);
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      crypto.getRandomValues(bytes);
    } else {
      for (let i = 0; i < len; i += 1) bytes[i] = Math.floor(Math.random() * 256);
    }
    return Array.from(bytes, (b) => chars[b % chars.length]).join('');
  };
  return `zc-${seg(6)}-${seg(6)}-${seg(6)}-${seg(4)}`;
}

/** Génère un slug lisible et unique à partir du titre de la réunion. */
function generateSlug(title: string): string {
  const base = title
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
  const suffix = Math.random().toString(36).slice(2, 8);
  return `${base || 'reunion'}-${suffix}`;
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Construit l'URL d'invitation publique sur le domaine de la boutique. */
function getMeetingUrl(
  inviteDomain: string,
  meeting: { slug: string | null; room_id: string },
): string {
  const basePath = (typeof __BASE_PATH__ !== 'undefined' ? __BASE_PATH__ : '').replace(/\/$/, '');
  const segment = meeting.slug || meeting.room_id;
  const path = meeting.slug ? 'zcall' : 'call';
  return `${inviteDomain}${basePath}/${path}/${segment}`;
}